import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/useToast';
import { useContracts, ContractPricing } from '@/hooks/useContracts';
import { renderBarcodeSVG } from '@/lib/barcode';
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  FileText,
  AlertTriangle,
  Calendar,
  DollarSign,
  Package,
  Barcode,
} from 'lucide-react';

interface ContractManagementPageProps {
  onSelectContract?: (contract: ContractPricing) => void;
}

export default function ContractManagementPage({ onSelectContract }: ContractManagementPageProps) {
  const { toast } = useToast();
  const {
    getContracts,
    getExpiringContracts,
    createContract,
    updateContract,
    deleteContract,
  } = useContracts();

  const [searchQuery, setSearchQuery] = React.useState('');
  const [showActiveOnly, setShowActiveOnly] = React.useState(true);
  const [selectedVendor, setSelectedVendor] = React.useState<string>('');
  const [showContractModal, setShowContractModal] = React.useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = React.useState(false);
  const [selectedContract, setSelectedContract] = React.useState<ContractPricing | null>(null);

  const { data: contractsData, isLoading } = getContracts({
    search: searchQuery || undefined,
    is_active: showActiveOnly,
    vendor_id: selectedVendor || undefined,
  });

  const { data: expiringContracts } = getExpiringContracts(30);

  const contracts = contractsData?.contracts || [];
  const totalContracts = contractsData?.total || 0;

  const handleViewContract = (contract: ContractPricing) => {
    setSelectedContract(contract);
    setShowContractModal(true);
  };

  const handleEditContract = (contract: ContractPricing) => {
    setSelectedContract(contract);
    setShowContractModal(true);
    toast({
      title: 'Edit Mode',
      description: `Editing contract: ${contract.contract_name}`,
    });
  };

  const handleDeleteContract = (contract: ContractPricing) => {
    deleteContract.mutate(contract.contract_id, {
      onSuccess: () => {
        toast({
          title: 'Contract Deleted',
          description: `${contract.contract_name} has been deactivated.`,
        });
      },
      onError: () => {
        toast({
          title: 'Error',
          description: 'Failed to delete contract',
          variant: 'destructive',
        });
      },
    });
  };

  const handleShowBarcodes = (contract: ContractPricing) => {
    setSelectedContract(contract);
    setShowBarcodeModal(true);
  };

  const getStatusBadge = (contract: ContractPricing) => {
    const now = new Date();
    const endDate = new Date(contract.end_date);
    const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (!contract.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (daysUntilExpiry < 0) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (daysUntilExpiry <= 30) {
      return <Badge variant="warning">Expiring Soon</Badge>;
    }
    return <Badge variant="success">Active</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
    }).format(amount);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contract Management</h1>
          <p className="text-muted-foreground">
            Manage vendor contracts, pricing agreements, and price lookups
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBarcodeModal(true)}>
            <Barcode className="h-4 w-4 mr-2" />
            Print Barcodes
          </Button>
          <Button
            onClick={() => {
              setSelectedContract(null);
              setShowContractModal(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Contract
          </Button>
        </div>
      </div>

      {/* Expiring Contracts Alert */}
      {expiringContracts && expiringContracts.length > 0 && (
        <Card className="border-warning bg-warning/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-warning">
              <AlertTriangle className="h-4 w-4" />
              Expiring Contracts ({expiringContracts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {expiringContracts.slice(0, 5).map((contract) => (
                <Badge key={contract.contract_id} variant="outline" className="whitespace-nowrap">
                  {contract.contract_name} - {contract.days_remaining} days
                </Badge>
              ))}
              {expiringContracts.length > 5 && (
                <Badge variant="outline">+{expiringContracts.length - 5} more</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Contracts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalContracts}</div>
            <p className="text-xs text-muted-foreground">
              {contracts.filter((c) => c.is_active).length} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Expiring This Month</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {expiringContracts?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">Next 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg. Discount</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {contracts.length > 0
                ? (
                    contracts.reduce((sum, c) => sum + c.discount_percentage, 0) /
                    contracts.length
                  ).toFixed(1)
                : 0}
              %
            </div>
            <p className="text-xs text-muted-foreground">From contract items</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Contracted Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {contracts.reduce((sum, c) => sum + c.items.length, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Total items covered</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contracts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Button
          variant={showActiveOnly ? 'default' : 'outline'}
          onClick={() => setShowActiveOnly(!showActiveOnly)}
        >
          {showActiveOnly ? 'Active Only' : 'Show All'}
        </Button>
      </div>

      {/* Contracts Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contract</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Loading contracts...
                  </TableCell>
                </TableRow>
              ) : contracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    No contracts found
                  </TableCell>
                </TableRow>
              ) : (
                contracts.map((contract) => (
                  <TableRow key={contract.contract_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{contract.contract_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {contract.contract_number}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{contract.vendor_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{contract.items.length} items</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{new Date(contract.start_date).toLocaleDateString()}</div>
                        <div className="text-muted-foreground">
                          to {new Date(contract.end_date).toLocaleDateString()}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(contract)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewContract(contract)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditContract(contract)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShowBarcodes(contract)}>
                            <Barcode className="h-4 w-4 mr-2" />
                            Print Barcode
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteContract(contract)}
                            className="text-error"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Contract Details Modal - Placeholder */}
      {showContractModal && selectedContract && (
        <ContractDetailsModal
          contract={selectedContract}
          open={showContractModal}
          onClose={() => {
            setShowContractModal(false);
            setSelectedContract(null);
          }}
        />
      )}

      {/* Barcode Print Modal - Placeholder */}
      {showBarcodeModal && (
        <BarcodePrintModal
          open={showBarcodeModal}
          onClose={() => setShowBarcodeModal(false)}
          contracts={contracts}
        />
      )}
    </div>
  );
}

// Contract Details Modal Component
function ContractDetailsModal({
  contract,
  open,
  onClose,
}: {
  contract: ContractPricing;
  open: boolean;
  onClose: () => void;
}) {
  const { formatCurrency } = useContracts();

  return (
    <div className={open ? 'fixed inset-0 z-50 flex items-center justify-center' : 'hidden'}>
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="fixed inset-4 md:inset-auto md:w-full md:max-w-2xl bg-background rounded-lg shadow-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{contract.contract_name}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <span className="sr-only">Close</span>
            ×
          </Button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Contract Number
                </label>
                <p className="mt-1">{contract.contract_number}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Vendor</label>
                <p className="mt-1">{contract.vendor_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Period</label>
                <p className="mt-1">
                  {new Date(contract.start_date).toLocaleDateString()} -{' '}
                  {new Date(contract.end_date).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Discount
                </label>
                <p className="mt-1">{contract.discount_percentage}%</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Items</label>
              <div className="mt-2 space-y-2">
                {contract.items.map((item) => (
                  <div
                    key={item.contract_item_id}
                    className="flex justify-between items-center p-2 bg-muted rounded"
                  >
                    <div>
                      <div className="font-medium">{item.item_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {item.item_code}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">
                        {formatCurrency(item.final_price)}
                      </div>
                      {item.discount_percentage > 0 && (
                        <div className="text-sm text-muted-foreground line-through">
                          {formatCurrency(item.unit_price)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {contract.notes && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Notes</label>
                <p className="mt-1">{contract.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Barcode Print Modal Component
function BarcodePrintModal({
  open,
  onClose,
  contracts,
}: {
  open: boolean;
  onClose: () => void;
  contracts: ContractPricing[];
}) {
  const [selectedContractId, setSelectedContractId] = React.useState<string>('');
  const selectedContract = contracts.find((c) => c.contract_id === selectedContractId);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="fixed inset-4 md:inset-auto md:w-full md:max-w-lg bg-background rounded-lg shadow-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Print Contract Barcodes</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <span className="sr-only">Close</span>
            ×
          </Button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-sm font-medium">Select Contract</label>
            <select
              className="mt-1 w-full p-2 border rounded"
              value={selectedContractId}
              onChange={(e) => setSelectedContractId(e.target.value)}
            >
              <option value="">Select a contract...</option>
              {contracts.map((contract) => (
                <option key={contract.contract_id} value={contract.contract_id}>
                  {contract.contract_name} - {contract.vendor_name}
                </option>
              ))}
            </select>
          </div>

          {selectedContract && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-medium">{selectedContract.contract_name}</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedContract.contract_number}
                </p>
              </div>

              {/* Barcode Preview */}
              <div className="space-y-2">
                {selectedContract.items.map((item) => (
                  <div
                    key={item.contract_item_id}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div>
                      <div className="font-medium text-sm">{item.item_name}</div>
                      <div className="text-xs text-muted-foreground">{item.item_code}</div>
                    </div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: renderBarcodeSVG(`CTR-${selectedContract.contract_id.substring(4)}-${item.item_id.substring(0, 4)}`, {
                          format: 'CODE128',
                          width: 150,
                          height: 50,
                          displayValue: true,
                          fontSize: 10,
                        }),
                      }}
                    />
                  </div>
                ))}
              </div>

              <Button className="w-full">
                <Barcode className="h-4 w-4 mr-2" />
                Print Selected Barcodes
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
